import { useToolbarPosition } from '@redturtle/qubicaamf-theme/helpers/hooks/useToolbarPosition.hook';
import {
  DialogTrigger,
  ModalOverlay,
  Dialog,
  Button,
  Modal,
  Disclosure,
  DisclosureGroup,
  DisclosurePanel,
  type PressEvent,
  type DisclosureGroupProps,
} from 'react-aria-components';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import backSVG from '@plone/volto/icons/back.svg';
import zoomSVG from '@plone/volto/icons/zoom.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import menuSVG from '@plone/volto/icons/menu.svg';
import styles from './styles.module.css';
import { useNavigationMenuState } from '@redturtle/qubicaamf-theme/helpers/hooks/useNavigationMenu.hook';
import { mergeData } from '@redturtle/qubicaamf-theme/helpers/dropdown-menu';
import type { OrderedSubMenuItem } from './MegaMenu';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import type { MenuItem } from '@redturtle/qubicaamf-theme/types/dropdown-menu';
import { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useScrollLock } from 'usehooks-ts';
import { useTransitionAnimation } from '@redturtle/qubicaamf-theme/helpers/hooks/useTransitionAnimation.hook';
import { useHistory } from 'react-router-dom';
import config from '@plone/registry';
import minilogo from '@redturtle/qubicaamf-theme/assets/qubicaminilogo.png';
import { defineMessages, useIntl } from 'react-intl';

import CtaIcon from '@redturtle/qubicaamf-theme/components/Icons/CtaIcon';

const messages = defineMessages({
  qubica_megamenu_open: {
    id: 'qubica_megamenu_open',
    defaultMessage: 'Apri il menu',
  },
  qubica_megamenu_navigate: {
    id: 'qubica_megamenu_navigate',
    defaultMessage: 'Sezioni di navigazione',
  },
  qubica_megamenu_search: {
    id: 'qubica_megamenu_search',
    defaultMessage: 'Cerca nel sito',
  },
  qubica_megamenu_back: {
    id: 'qubica_megamenu_back',
    defaultMessage: 'Torna al livello superiore',
  },
  qubica_megamenu_close: {
    id: 'qubica_megamenu_close',
    defaultMessage: 'Chiudi il menu',
  },
});

type MobileDropdownMenuItemProps = {
  item?: OrderedSubMenuItem | MenuItem;
  open: () => void;
  close: () => void;
  defaultExpandedKeys?: DisclosureGroupProps['defaultExpandedKeys'];
  expandedKeys?: DisclosureGroupProps['expandedKeys'];
  setActiveMenuItem: React.Dispatch<
    React.SetStateAction<OrderedSubMenuItem | MenuItem>
  >;
  disableTabbing?: boolean;
};
const MobileDropdownMenuItem: React.FC<MobileDropdownMenuItemProps> = ({
  item,
  open,
  close,
  defaultExpandedKeys = [],
  expandedKeys = undefined,
  setActiveMenuItem,
  disableTabbing,
}) => {
  if (!item) return null;
  let tabProps = {};
  if (disableTabbing)
    tabProps = { tabIndex: -1, 'aria-disabled': true, disabled: true };
  const handlePress =
    (item: MobileDropdownMenuItemProps['item']) => (_e: PressEvent) => {
      setActiveMenuItem(item);
      open();
    };

  if (item.mode === 'dropdown')
    return (
      <DisclosureGroup
        defaultExpandedKeys={defaultExpandedKeys}
        expandedKeys={expandedKeys}
        className={cx('react-aria-DisclosureGroup', styles['disclosure-group'])}
      >
        <Disclosure
          id={item['@id']}
          className={cx('react-aria-Disclosure', {
            [styles['disclosure']]: !expandedKeys,
          })}
        >
          <Button
            slot="trigger"
            className={cx(
              'react-aria-Button',
              styles['submenu-sheet-nav-list-button'],
            )}
            isDisabled={
              (expandedKeys && !!Array.from(expandedKeys).length) ||
              disableTabbing
            }
          >
            {item.title}
            <svg
              viewBox="0 0 24 24"
              className={styles['submenu-sheet-nav-list-button-svg']}
              style={{ strokeWidth: 5, width: '1.25rem', height: '1.25rem' }}
            >
              <path d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </Button>
          <DisclosurePanel>
            <ul
              className={cx(
                styles['submenu-sheet-nav-list'],
                styles['no-list-styles'],
              )}
            >
              {item.items?.map((it: OrderedSubMenuItem) => {
                // TODO check type
                let Component = UniversalLink as unknown as any;
                let props: any = {
                  href: flattenToAppURL(it['@id']),
                  className: cx(
                    styles['submenu-sheet-nav-list-button'],
                    styles['bold'],
                  ),
                  onClick: close,
                };
                if (it.items.length > 0) {
                  Component = Button;
                  props = {
                    ...props,
                    onPress: handlePress(it),
                    slot: 'trigger',
                    isDisabled: disableTabbing,
                  };
                }
                const isButton = Object.hasOwn(props, 'onPress');

                return (
                  <li
                    key={it['@id']}
                    id={it['@id']}
                    className={styles['submenu-sheet-nav-list-item']}
                    {...tabProps}
                  >
                    <Component {...tabProps} {...props}>
                      <span className={styles.align_to_left}>{it.title}</span>
                      {isButton && (
                        <svg
                          viewBox="0 0 24 24"
                          className={
                            styles['submenu-sheet-nav-list-button-svg']
                          }
                        >
                          <path d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                        </svg>
                      )}
                    </Component>
                  </li>
                );
              })}
            </ul>
            {item.showMoreLink?.length > 0 && item?.showMoreText ? (
              <div className={styles['showmore']}>
                <UniversalLink
                  href={item.showMoreLink[0]?.['@id']}
                  className={styles['showmore-cta']}
                  onClick={close}
                  openLinkInNewTab={false}
                >
                  <CtaIcon iconValue={item.showMoreIcon} /> {item.showMoreText}
                </UniversalLink>
              </div>
            ) : null}
          </DisclosurePanel>
        </Disclosure>
      </DisclosureGroup>
    );
  else if (item.mode === 'simpleLink')
    return (
      <UniversalLink
        href={flattenToAppURL(item.linkUrl?.[0]?.['@id'])}
        key={flattenToAppURL(item.linkUrl?.[0]?.['@id'])}
        className={cx(
          styles['dropdown-menu-navitem'],
          styles['bold'],
          styles['sheet-link'],
        )}
        onClick={close}
        openLinkInNewTab={false}
        {...tabProps}
      >
        {item.title}
      </UniversalLink>
    );
  else
    return (
      <UniversalLink
        href={flattenToAppURL(item['@id'])}
        key={flattenToAppURL(item['@id'])}
        className={cx(
          styles['dropdown-menu-navitem'],
          styles['bold'],
          styles['sheet-link'],
        )}
        onClick={close}
        openLinkInNewTab={false}
        {...tabProps}
      >
        {item.title}
      </UniversalLink>
    );
};

const MobileMegamenuNavigation: React.FC<
  Pick<MobileDropdownMenuItemProps, 'close'>
> = ({ close: closeModal }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const intl = useIntl();
  const { dropdownMenuNavItems, navItems } = useNavigationMenuState();
  const { items } = dropdownMenuNavItems[0];
  const [activeMenuItem, setActiveMenuItem] = useState<
    OrderedSubMenuItem | MenuItem
  >();
  useScrollLock();
  const { status, isVisible, close, open } = useTransitionAnimation();
  const handleBackPress = (_e: PressEvent) => {
    close();
  };
  useEffect(() => {
    if (ref.current && isVisible) ref.current.focus();
  }, [isVisible, ref]);

  let liProps = {};
  if (isVisible)
    liProps = {
      tabIndex: -1,
    };
  return (
    <div className={styles['nav-container']}>
      <nav
        aria-hidden={!isVisible}
        className={styles['submenu-sheet']}
        data-entering={status === 'entering' ? 'true' : undefined} // Quando sta entrando
        data-exiting={status === 'exiting' ? 'true' : undefined} // Quando sta uscendo
        onAnimationEnd={() => {
          if (status === 'exiting') {
            close();
          }
        }}
        aria-live={'polite'}
        aria-label={intl.formatMessage(messages.qubica_megamenu_navigate)}
      >
        <div className={styles['go-back']}>
          <Button
            onPress={handleBackPress}
            style={{ border: 'none', padding: 0 }}
            ref={ref}
            isDisabled={!isVisible}
            aria-label={intl.formatMessage(messages.qubica_megamenu_back)}
          >
            <Icon name={backSVG} size="24px" />
          </Button>
        </div>
        <MobileDropdownMenuItem
          item={{ ...activeMenuItem, mode: 'dropdown' }}
          open={open}
          close={closeModal}
          defaultExpandedKeys={activeMenuItem ? [activeMenuItem?.['@id']] : []}
          expandedKeys={activeMenuItem ? [activeMenuItem?.['@id']] : []}
          setActiveMenuItem={setActiveMenuItem}
          disableTabbing={!isVisible}
        />
      </nav>
      <nav aria-hidden={isVisible} aria-live={'polite'}>
        <ul className={styles['no-list-styles']}>
          {items?.map((menuItem, index) => {
            let navItem = menuItem;
            if (menuItem.mode === 'dropdown') {
              const navRoot =
                Array.isArray(menuItem.navigationRoot) &&
                menuItem.navigationRoot.length > 0
                  ? menuItem.navigationRoot[0]
                  : {};
              const navigationResultsForNavroot =
                navItems.find((ni) => ni['@id'] === navRoot['@id'])?.items ??
                [];
              navItem = {
                ...menuItem,
                items: mergeData(navigationResultsForNavroot, menuItem.items),
              };
            }
            return (
              <li key={index} {...liProps} className={styles.nav_item}>
                <MobileDropdownMenuItem
                  item={navItem}
                  open={open}
                  close={closeModal}
                  setActiveMenuItem={setActiveMenuItem}
                  disableTabbing={isVisible}
                />
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

const MobileMegaMenu: React.FC = () => {
  const Image = config.getComponent('Image').component;
  const intl = useIntl();
  const position = useToolbarPosition();
  const history = useHistory();
  const [formState, setFormState] = useState({
    search: '',
  });
  const search = useCallback(
    (close: () => void) => (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      close();
      history.push(
        `/search?SearchableText=${encodeURIComponent(formState.search)}`,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formState],
  );
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormState({ search: value });
  };
  return (
    <DialogTrigger>
      <Button
        className={styles.trigger}
        aria-label={intl.formatMessage(messages.qubica_megamenu_open)}
      >
        <Icon name={menuSVG} size="36px" />
      </Button>
      <ModalOverlay
        className={styles['my-overlay']}
        style={{ ...position }}
        isDismissable
      >
        <Modal className={styles['my-modal']}>
          <Dialog className={cx('react-aria-Dialog', styles['dialog'])}>
            {({ close }) => (
              <div className={styles['container']}>
                <div className={styles.logo_nav_wrapper}>
                  <div className={styles.logo}>
                    <UniversalLink href="/" openLinkInNewTab={false}>
                      <Image src={minilogo} alt="Qubica AMF Logo" />
                    </UniversalLink>
                  </div>
                </div>
                <form onSubmit={search(close)} className={styles['searchbox']}>
                  <label htmlFor="search" hidden>
                    {intl.formatMessage(messages.qubica_megamenu_search)}
                  </label>
                  <Icon
                    name={zoomSVG}
                    size="24px"
                    className={styles['searchicon']}
                  />
                  <Button
                    type="submit"
                    className={styles['searchbutton']}
                  ></Button>
                  <input
                    id="search"
                    name="search "
                    type="text"
                    placeholder={intl.formatMessage(
                      messages.qubica_megamenu_search,
                    )}
                    onChange={handleInputChange}
                    className={styles['searchinput']}
                  />
                </form>
                <MobileMegamenuNavigation close={close} />
                <div className={styles['nav-container-extras']}>
                  <div
                    className={cx(styles.contact_us, styles['showmore-cta'])}
                  >
                    <UniversalLink href="/contact" openLinkInNewTab={false}>
                      Contact Us
                    </UniversalLink>
                  </div>
                </div>
                <Button
                  onPress={close}
                  className={styles['navmenu-close']}
                  aria-label={intl.formatMessage(
                    messages.qubica_megamenu_close,
                  )}
                >
                  <Icon name={clearSVG} size="24px" />
                </Button>
              </div>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
};
export { MobileMegaMenu };
